import { useEffect, useState } from "react";
import { useLeadDetail } from "../hooks/useLeadDetail";
import { presalesApi } from "../api/presalesApi";
import BasicTab from "./tabs/BasicTab";
import PricingTab from "./tabs/PricingTab";
import ScopeTab from "./tabs/ScopeTab";
import RemindersTab from "./tabs/RemindersTab";
import SpendTab from "./tabs/SpendTab";
import EntityDiscussionsTab from "../../common/components/EntityDiscussionsTab";
import DrawerTabs from "../../common/components/DrawerTabs";
import "../../../DefectsViewV2.css";

function rmdCounts(lead) {
  const reminders = lead?.reminders || [];
  const open  = reminders.filter((r) => !r.is_done);
  const TODAY = new Date().toISOString().slice(0, 10);
  const over  = open.filter((r) => r.due_date < TODAY).length;
  const due   = open.filter((r) => r.due_date >= TODAY && daysDiff(r.due_date, TODAY) <= 2).length;
  return { count: open.length, over, due };
}

function daysDiff(a, b) {
  return Math.round((new Date(a) - new Date(b)) / 86400000);
}

function inScopeCount(lead) {
  const products = lead?.sow_products || [];
  return products.flatMap((p) => p.modules || []).filter((m) => m.state === "in").length;
}

export default function PresalesSlideover({ leadId, onClose, onLeadChange, showToast }) {
  const { lead, loading, error, mutate } = useLeadDetail(leadId);
  const [tab, setTab] = useState("basic");
  const [closingStage, setClosingStage] = useState(null);

  useEffect(() => {
    setTab("basic");
  }, [leadId]);

  async function markClosed(stage) {
    setClosingStage(stage);
    try {
      await presalesApi.setStage(leadId, stage);
      await mutate();
      onLeadChange?.();
      showToast?.(`Marked as ${stage}`, "success");
    } catch (err) {
      showToast?.(err.message || "Failed to update stage", "error");
    } finally {
      setClosingStage(null);
    }
  }

  const rmd   = lead ? rmdCounts(lead) : { count: 0, over: 0, due: 0 };
  const scopeIn = lead ? inScopeCount(lead) : 0;
  const isClosed = lead?.stage === "won" || lead?.stage === "lost";

  return (
    <>
      <div className={`v2drw-scrim ps-so-scrim on`} onClick={onClose} />
      <div className={`v2drw ps-slideover on`} role="dialog" aria-modal="true">
        {/* Header */}
        <div className="v2drw-head ps-so-hd">
          <div className="ps-so-hd-who">
            <div className="ps-so-eyebrow">
              {lead?.lead_number && <span>{lead.lead_number}</span>}
              {lead?.source && <><span className="sep">·</span><span>{lead.source}</span></>}
            </div>
            <h2 className="v2drw-title ps-so-name">
              {loading ? "Loading…" : (lead?.contact_name || "—")}
            </h2>
            {lead && (
              <div className="ps-so-sub">
                {lead.designation && `${lead.designation} · `}{lead.company_name}
              </div>
            )}
          </div>
          <button className="v2drw-close ps-so-close" onClick={onClose}>✕</button>
        </div>

        {/* Tabs */}
        <DrawerTabs
          tabs={[
            { key: "basic",   label: "Basic",   icon: "info" },
            { key: "pricing", label: "Pricing", icon: "pricing" },
            { key: "scope",   label: "Scope",   icon: "scope", badge: scopeIn || null },
            {
              key: "rmd",
              label: "Reminders",
              icon: "reminders",
              badge: rmd.count || null,
              badgeTone: rmd.over ? "danger" : rmd.due ? "warn" : undefined,
            },
            { key: "spend", label: "Spend", icon: "spend", badge: (lead?.spend?.length) || null },
            { key: "notes", label: "Notes/Comments", icon: "notes" },
          ]}
          activeTab={tab}
          onChange={setTab}
        />

        {/* Body */}
        <div className="v2drw-body ps-so-body">
          {loading && <div className="ps-loading">Loading…</div>}
          {error   && <div className="ps-loading" style={{ color: "var(--ps-red)" }}>{error}</div>}
          {lead && !loading && (
            <>
              <div className={`ps-so-panel ${tab === "basic"   ? "on" : ""}`}>
                <BasicTab   lead={lead} mutate={async () => { await mutate(); onLeadChange?.(); }} showToast={showToast} />
              </div>
              <div className={`ps-so-panel ${tab === "pricing" ? "on" : ""}`}>
                <PricingTab lead={lead} mutate={async () => { await mutate(); onLeadChange?.(); }} showToast={showToast} />
              </div>
              <div className={`ps-so-panel ${tab === "scope"   ? "on" : ""}`}>
                <ScopeTab   lead={lead} mutate={async () => { await mutate(); onLeadChange?.(); }} showToast={showToast} />
              </div>
              <div className={`ps-so-panel ${tab === "rmd"     ? "on" : ""}`}>
                <RemindersTab lead={lead} mutate={async () => { await mutate(); onLeadChange?.(); }} showToast={showToast} />
              </div>
              <div className={`ps-so-panel ${tab === "spend"   ? "on" : ""}`}>
                <SpendTab   lead={lead} mutate={async () => { await mutate(); onLeadChange?.(); }} showToast={showToast} />
              </div>
              {tab === "notes" && (
                <div className="ps-so-panel on">
                  <EntityDiscussionsTab entityType="presales_lead" entityId={lead.id} showToast={showToast} />
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer: Won / Lost */}
        {lead && !isClosed && (
          <div className="ps-so-footer">
            <button
              className="lost"
              disabled={!!closingStage}
              onClick={() => markClosed("lost")}
            >
              Mark as Lost
            </button>
            <button
              className="won"
              disabled={!!closingStage}
              onClick={() => markClosed("won")}
            >
              Mark as Won
            </button>
          </div>
        )}
        {lead && isClosed && (
          <div className="ps-so-footer">
            <span style={{ flex: 1, fontFamily: "var(--ps-mono)", fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase", color: lead.stage === "won" ? "var(--ps-green)" : "var(--ps-red)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {lead.stage === "won" ? "✓ Won" : "✕ Lost"}
            </span>
          </div>
        )}
      </div>
    </>
  );
}
